// Category metadata + branching follow-up questions used by the "What are
// you sending?" and "Contents check" wizard steps. Plain-English, no
// dangerous-goods jargon exposed to the sender.

import type { ContentCategory } from "../types";

export interface CategoryQuestion {
  key: string;
  label: string;
  helpText?: string;
  type: "boolean" | "select";
  options?: { value: string; label: string }[];
  /** Only show this question if a previous answer in the same category matches. */
  showIf?: { key: string; equals: boolean | string | number };
}

export interface CategoryDefinition {
  category: ContentCategory;
  label: string;
  shortLabel: string;
  icon: string; // name of an icon component rendered by the UI
  helpText?: string;
  questions: CategoryQuestion[];
}

export const CATEGORY_DEFINITIONS: CategoryDefinition[] = [
  {
    category: "DOCUMENTS_LETTERS",
    label: "Letter, card or documents",
    shortLabel: "Letter / Card",
    icon: "mail",
    questions: [],
  },
  {
    category: "GENERAL_GOODS",
    label: "Other everyday item",
    shortLabel: "Other item",
    icon: "package",
    helpText: "Books, games, keepsakes, household items, and other ordinary goods that do not fit a specialist category.",
    questions: [],
  },
  {
    category: "CLOTHING",
    label: "Clothes",
    shortLabel: "Clothes",
    icon: "shirt",
    questions: [],
  },
  {
    category: "FOOD_AMBIENT",
    label: "Food or snacks (ambient, non-perishable)",
    shortLabel: "Food / Snacks",
    icon: "cookie",
    questions: [
      {
        key: "isChocolate",
        label: "Does it include chocolate or confectionery that could melt?",
        type: "boolean",
      },
      {
        key: "isPerishable",
        label: "Does it need to stay refrigerated or eaten within a few days?",
        type: "boolean",
      },
      {
        key: "isLiquid",
        label: "Does it include any liquid (sauce, oil, drink)?",
        type: "boolean",
      },
      {
        key: "isGlassContainer",
        label: "Is it in a glass container?",
        type: "boolean",
        showIf: { key: "isLiquid", equals: true },
      },
    ],
  },
  {
    category: "FOOD_PERISHABLE",
    label: "Perishable or chilled food",
    shortLabel: "Perishable Food",
    icon: "thermometer",
    helpText: "Food that needs to stay cold or be eaten quickly, e.g. fresh baking, dairy.",
    questions: [],
  },
  {
    category: "ELECTRONICS_NO_BATTERY",
    label: "Electronics",
    shortLabel: "Electronics",
    icon: "cpu",
    questions: [
      {
        key: "hasBattery",
        label: "Does it contain a battery?",
        type: "boolean",
      },
      {
        key: "batteryType",
        label: "What kind of battery?",
        type: "select",
        showIf: { key: "hasBattery", equals: true },
        options: [
          { value: "lithium_installed", label: "Lithium — installed inside the device" },
          { value: "lithium_loose", label: "Lithium — loose / spare battery" },
          { value: "power_bank", label: "It's a power bank / portable charger" },
          { value: "alkaline", label: "Ordinary AA / AAA / button batteries" },
        ],
      },
      {
        key: "batteryDamaged",
        label: "Is the battery damaged, swollen, or has it been recalled?",
        type: "boolean",
        showIf: { key: "hasBattery", equals: true },
      },
    ],
  },
  {
    category: "MEDICINE_OTC",
    label: "Medicine (over the counter)",
    shortLabel: "Medicine",
    icon: "pill",
    questions: [
      {
        key: "isPrescription",
        label: "Is this a prescription-only medicine?",
        type: "boolean",
      },
      {
        key: "isControlled",
        label: "Is it a controlled drug (as classified by the Home Office)?",
        type: "boolean",
        helpText: "If you're not sure, answer yes so we can flag this for a manual check.",
      },
      {
        key: "isLiquidMedicine",
        label: "Is it a liquid or aerosol (e.g. inhaler, spray, syrup)?",
        type: "boolean",
      },
    ],
  },
  {
    category: "TOILETRIES_PERFUME_AFTERSHAVE",
    label: "Toiletries, perfume or aftershave",
    shortLabel: "Toiletries",
    icon: "spray",
    questions: [
      {
        key: "isAerosol",
        label: "Is it a pressurised aerosol can?",
        type: "boolean",
      },
      {
        key: "aerosolLimit",
        label: "If it is an aerosol, how much are you sending?",
        type: "select",
        showIf: { key: "isAerosol", equals: true },
        options: [
          { value: "max_2_each_500ml", label: "No more than 2 cans, each 500ml or less" },
          { value: "over_limit", label: "More than that" },
          { value: "unknown", label: "I don't know" },
        ],
      },
      {
        key: "isFlammableLiquid",
        label: "Does it contain alcohol/perfume concentrate (flammable liquid)?",
        type: "boolean",
      },
      {
        key: "isOrdinaryLiquid",
        label: "Is it a non-flammable liquid or roll-on rather than a solid stick?",
        type: "boolean",
        showIf: { key: "isFlammableLiquid", equals: false },
      },
      {
        key: "volumeBand",
        label: "How much liquid is in the parcel?",
        type: "select",
        showIf: { key: "isOrdinaryLiquid", equals: true },
        options: [
          { value: "up_to_1l", label: "1 litre or less in total" },
          { value: "over_1l_to_10l", label: "More than 1 litre" },
          { value: "unknown", label: "I don't know" },
        ],
      },
      {
        key: "sealedAndLeakproof",
        label: "Is every container sealed and packed so it cannot leak?",
        type: "boolean",
        showIf: { key: "isOrdinaryLiquid", equals: true },
      },
      {
        key: "perfumeLimit",
        label: "If it is perfume or aftershave, how much are you sending?",
        type: "select",
        showIf: { key: "isFlammableLiquid", equals: true },
        options: [
          { value: "max_4_each_150ml", label: "No more than 4 containers, each 150ml or less" },
          { value: "over_limit", label: "More than that" },
          { value: "unknown", label: "I don't know" },
        ],
      },
    ],
  },
  {
    category: "ALCOHOL",
    label: "Alcohol",
    shortLabel: "Alcohol",
    icon: "wine",
    questions: [
      {
        key: "abvBand",
        label: "What is its alcohol strength?",
        type: "select",
        options: [
          { value: "up_to_24", label: "24% ABV or less (beer, wine, cider)" },
          { value: "24_to_70", label: "Over 24% and no more than 70% ABV (most spirits)" },
          { value: "over_70", label: "Over 70% ABV" },
          { value: "unknown", label: "I don't know" },
        ],
      },
    ],
  },
  {
    category: "JEWELLERY_VALUABLES",
    label: "Jewellery, cash or valuable items",
    shortLabel: "Valuables",
    icon: "gem",
    questions: [
      {
        key: "includesCash",
        label: "Does the parcel include cash or vouchers?",
        type: "boolean",
      },
    ],
  },
  {
    category: "FRAGILE_ITEMS",
    label: "Fragile items",
    shortLabel: "Fragile",
    icon: "wine-glass",
    questions: [],
  },
  {
    category: "BLADES_SHARPS",
    label: "Blades or sharp objects",
    shortLabel: "Blades / Sharps",
    icon: "scissors",
    questions: [
      {
        key: "sharpType",
        label: "What type is it?",
        type: "select",
        options: [
          { value: "ordinary_sharp", label: "Scissors, sewing needles, cutlery or similar" },
          { value: "bladed_article", label: "Knife, loose razor blade or other bladed article" },
          { value: "cartridge_razor", label: "Cartridge or disposable shaving razor" },
          { value: "electric_razor", label: "Electric shaver" },
          { value: "unknown", label: "I'm not sure" },
        ],
      },
      {
        key: "isBladeOver3Inch",
        label: "Is any blade longer than 7.62cm (3 inches)?",
        type: "boolean",
        showIf: { key: "sharpType", equals: "bladed_article" },
        helpText: "Knives with blades over this length are treated as offensive weapons for postal purposes.",
      },
    ],
  },
  {
    category: "OTHER_DANGEROUS_GOODS",
    label: "Something unusual or restricted",
    shortLabel: "Something unusual",
    icon: "alert-triangle",
    helpText: "Compressed gas, flammables, chemicals, biological samples, plants/seeds, tobacco/nicotine, magnets, weapons, or anything not listed above.",
    questions: [
      {
        key: "specificType",
        label: "What best describes it?",
        type: "select",
        options: [
          { value: "compressed_gas", label: "Compressed gas / gas canister" },
          { value: "flammable", label: "Flammable liquid or solid" },
          { value: "chemical", label: "Chemical or lab sample" },
          { value: "biological", label: "Biological material" },
          { value: "plants_seeds", label: "Plants or seeds" },
          { value: "tobacco_nicotine", label: "Tobacco or nicotine products" },
          { value: "magnets", label: "Strong magnets" },
          { value: "weapons_explosives", label: "Weapons, ammunition, or explosives" },
          { value: "firearm", label: "Firearm" },
          { value: "ammunition", label: "Ammunition" },
          { value: "prohibited_weapon", label: "Prohibited weapon" },
          { value: "explosive", label: "Explosive or pyrotechnic" },
          { value: "replica_ordnance", label: "Replica, inert or deactivated ordnance" },
          { value: "christmas_crackers", label: "Christmas or party crackers" },
          { value: "life_jacket", label: "Life jacket or buoyancy aid" },
          { value: "life_jacket_no_gas", label: "Solid/non-inflatable life jacket with no gas cartridge" },
          { value: "ashes", label: "Human or animal ashes" },
          { value: "live_animal", label: "Live animal" },
          { value: "counterfeit", label: "Counterfeit or pirated goods" },
          { value: "obscene_illegal", label: "Obscene or illegal material" },
          { value: "waste", label: "Waste or refuse" },
          { value: "spillable_battery", label: "Vehicle or spillable battery" },
          { value: "radioactive", label: "Radioactive material" },
          { value: "hazard_pictogram", label: "Item with an unexplained hazard pictogram" },
          { value: "unsafe_packaging", label: "Unsafe, damaged or leaking packaging" },
          { value: "other", label: "Something else" },
        ],
      },
      {
        key: "ashesWeight",
        label: "How much ash is being sent?",
        type: "select",
        showIf: { key: "specificType", equals: "ashes" },
        options: [
          { value: "up_to_50g", label: "50g or less" },
          { value: "over_50g", label: "Over 50g" },
          { value: "unknown", label: "I don't know" },
        ],
      },
    ],
  },
  { category: "CARDS", label: "Card or postcard", shortLabel: "Card", icon: "mail", questions: [] },
  { category: "FOOD_CHOCOLATE_CONFECTIONERY", label: "Chocolate or confectionery", shortLabel: "Chocolate", icon: "cookie", questions: [] },
  {
    category: "MEDICINE_PRESCRIPTION",
    label: "Prescription medicine",
    shortLabel: "Prescription medicine",
    icon: "pill",
    questions: [
      { key: "isControlled", label: "Is it a controlled drug?", type: "boolean", helpText: "If you are unsure, choose Yes so it is checked before posting." },
      { key: "isToxicOrFlammable", label: "Is it toxic or flammable?", type: "boolean" },
      { key: "scientificPurpose", label: "Is it being sent for a scientific or laboratory purpose?", type: "boolean" },
    ],
  },
  {
    category: "LIQUIDS_GENERAL",
    label: "Non-dangerous liquid",
    shortLabel: "Liquid",
    icon: "package",
    questions: [
      { key: "volumeBand", label: "How much liquid is in the whole parcel?", type: "select", options: [
        { value: "up_to_1l", label: "1 litre or less" },
        { value: "over_1l_to_10l", label: "Over 1 litre and no more than 10 litres" },
        { value: "over_10l", label: "Over 10 litres" },
        { value: "unknown", label: "I don't know" },
      ] },
      { key: "sealedAndLeakproof", label: "Is every container sealed, leakproof and cushioned?", type: "boolean" },
    ],
  },
  {
    category: "AEROSOLS_DEODORANT",
    label: "Toiletry or medicinal aerosol",
    shortLabel: "Aerosol",
    icon: "spray",
    questions: [{ key: "aerosolLimit", label: "How much are you sending?", type: "select", options: [
      { value: "max_2_each_500ml", label: "No more than 2 cans, each 500ml or less" },
      { value: "over_limit", label: "More than that" },
      { value: "unknown", label: "I don't know" },
    ] }],
  },
  {
    category: "NAIL_POLISH",
    label: "Nail polish or varnish",
    shortLabel: "Nail polish",
    icon: "package",
    questions: [{ key: "polishLimit", label: "How much are you sending?", type: "select", options: [
      { value: "max_4_each_30ml", label: "No more than 4 bottles, each 30ml or less" },
      { value: "over_limit", label: "More than that" },
      { value: "unknown", label: "I don't know" },
    ] }],
  },
  {
    category: "ELECTRONICS_INSTALLED_BATTERY",
    label: "Device with an installed battery",
    shortLabel: "Battery device",
    icon: "cpu",
    questions: [
      { key: "batterySafety", label: "Which best describes the battery?", type: "select", options: [
        { value: "consumer_under_limits", label: "Undamaged phone/laptop/tablet-style battery, under 100Wh" },
        { value: "large_or_unknown", label: "Larger than 100Wh, unusual, or I don't know" },
        { value: "damaged_recalled", label: "Damaged, swollen or recalled" },
      ] },
      { key: "installedAndProtected", label: "Is it installed in the device and protected against switching on?", type: "boolean" },
    ],
  },
  { category: "LITHIUM_BATTERY_LOOSE", label: "Loose or spare lithium battery", shortLabel: "Loose lithium battery", icon: "alert-triangle", questions: [] },
  { category: "POWER_BANK", label: "Power bank or portable charger", shortLabel: "Power bank", icon: "alert-triangle", questions: [] },
  {
    category: "HOUSEHOLD_BATTERIES_AA_AAA",
    label: "Household or other battery",
    shortLabel: "Batteries",
    icon: "package",
    questions: [{ key: "batterySituation", label: "Which best describes them?", type: "select", options: [
      { value: "new_small_max8_original", label: "New AA-size or smaller, maximum 8, in original packaging" },
      { value: "new_other", label: "New but not as described above" },
      { value: "used", label: "Used" },
      { value: "nonspillable_new", label: "New sealed non-spillable battery" },
      { value: "spillable_vehicle", label: "Vehicle, wet-cell or spillable battery" },
      { value: "unknown", label: "I don't know" },
    ] }],
  },
  {
    category: "MAGNETS", label: "Magnet", shortLabel: "Magnet", icon: "package", questions: [
      { key: "magnetStrength", label: "Has its field strength been measured at 4.6 metres?", type: "select", options: [
        { value: "below_threshold", label: "Yes — below 0.418 A/m at 4.6m" },
        { value: "above_threshold", label: "Yes — at or above 0.418 A/m at 4.6m" },
        { value: "consumer_unknown", label: "No — small consumer magnet" },
        { value: "strong_or_unmeasured", label: "No — strong/industrial magnet" },
      ] },
    ],
  },
  { category: "CASH", label: "Cash, coins or banknotes", shortLabel: "Cash", icon: "package", questions: [] },
  { category: "COMPRESSED_GAS", label: "Compressed gas or non-toiletry aerosol", shortLabel: "Compressed gas", icon: "alert-triangle", questions: [] },
  { category: "FLAMMABLES", label: "Flammable item", shortLabel: "Flammable item", icon: "alert-triangle", questions: [] },
  {
    category: "CHEMICALS", label: "Chemical or environmental sample", shortLabel: "Chemical", icon: "alert-triangle", questions: [
      { key: "chemicalType", label: "What type is it?", type: "select", options: [
        { value: "environmental_sample", label: "Environmentally hazardous sample for analysis" },
        { value: "corrosive", label: "Corrosive" },
        { value: "oxidiser", label: "Oxidising chemical" },
        { value: "toxic", label: "Toxic or poisonous" },
        { value: "pesticide", label: "Pesticide or weedkiller" },
        { value: "unknown", label: "Something else / I don't know" },
      ] },
    ],
  },
  {
    category: "BIOLOGICAL_MATERIAL", label: "Biological or patient sample", shortLabel: "Biological sample", icon: "alert-triangle", questions: [
      { key: "biologicalType", label: "What type is it?", type: "select", options: [
        { value: "category_b", label: "UN3373 Category B biological substance" },
        { value: "patient_specimen", label: "Patient specimen requested by a medical professional" },
        { value: "clinical_waste", label: "Clinical waste or used sharps" },
        { value: "unknown", label: "Something else / I don't know" },
      ] },
    ],
  },
  {
    category: "PLANTS_SEEDS", label: "Plant, seed, soil or wildlife product", shortLabel: "Plant / Seed", icon: "package", questions: [
      { key: "plantType", label: "What type is it?", type: "select", options: [
        { value: "plant", label: "Live plant, cutting, root or bulb" },
        { value: "seeds", label: "Seeds" },
        { value: "soil_sample", label: "Soil sample for analysis" },
        { value: "cites", label: "Wildlife product that may need a CITES permit" },
        { value: "unknown", label: "Something else / I don't know" },
      ] },
    ],
  },
  { category: "TOBACCO_NICOTINE", label: "Tobacco, vape or nicotine product", shortLabel: "Tobacco / Vape", icon: "alert-triangle", questions: [] },
];

export function findCategoryDefinition(category: ContentCategory): CategoryDefinition | undefined {
  return CATEGORY_DEFINITIONS.find((c) => c.category === category);
}

/** Categories offered as visual tiles on the "What are you sending?" step. */
export const PRIMARY_CATEGORY_TILES: ContentCategory[] = [
  "DOCUMENTS_LETTERS",
  "CLOTHING",
  "FOOD_AMBIENT",
  "ELECTRONICS_NO_BATTERY",
  "MEDICINE_OTC",
  "TOILETRIES_PERFUME_AFTERSHAVE",
  "JEWELLERY_VALUABLES",
  "OTHER_DANGEROUS_GOODS",
];
